import React from "react";
import { Warning } from "@phosphor-icons/react";

/**
 * Safety & responsibility disclaimer block. Used on:
 *   • Landing page (full version)
 *   • Signup flow (full version above CTA)
 *   • Maintenance / Airworthiness / Parts pages (compact variant)
 *
 * Props:
 *   variant: "full" | "compact"  (default "full")
 *   className: extra wrapper classes
 */
export default function Disclaimer({ variant = "full", className = "" }) {
  if (variant === "compact") {
    return (
      <div className={`flex items-start gap-3 p-3 border border-amber/30 bg-amber/5 text-[11px] text-amber/90 leading-relaxed ${className}`} data-testid="disclaimer-compact">
        <Warning size={14} weight="fill" className="text-amber mt-0.5 shrink-0" />
        <span>
          <strong>Not a substitute for qualified personnel.</strong> OwnerCAM is a
          tracking tool — built for private owner-operators of non-commercial,
          non-complex aircraft. Always verify with your licensed mechanic
          (and your CAMO if one is required for your aircraft) before relying on
          any data shown here for an airworthiness decision.
        </span>
      </div>
    );
  }

  return (
    <div className={`border border-amber/30 bg-amber/5 p-7 lg:p-9 ${className}`} data-testid="disclaimer-full">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 border border-amber flex items-center justify-center shrink-0">
          <Warning size={20} weight="fill" className="text-amber" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber font-mono mb-2">
            Safety & responsibility
          </div>
          <h3 className="text-xl lg:text-2xl font-semibold tracking-tight mb-3 text-zinc-100">
            OwnerCAM does not replace your mechanic, CAMO (where required), or regulatory responsibility.
          </h3>
          <p className="text-sm text-zinc-300 leading-relaxed">
            OwnerCAM is a maintenance tracking and aircraft ownership management
            tool, designed for private owner-operators of non-commercial, non-complex
            aircraft (typically managed under EASA Part-ML or equivalent national
            rules — Cessna 172, PA-28, DA40, SR20, TB-10 and similar). A certified
            CAMO is normally required only for AOC / Part-CAT, commercial specialised /
            ATO operations, or complex motor-powered aircraft — if any of those apply
            to you, OwnerCAM does <strong>not</strong> replace your contracted CAMO.
            In all cases OwnerCAM does not replace the aircraft owner/operator's
            legal responsibilities, an approved maintenance program, a licensed
            mechanic, a Part-145 organization, an A&amp;P/IA, or any applicable
            aviation authority requirement. Always verify maintenance, AD compliance,
            and airworthiness decisions with qualified personnel and official source
            documents.
          </p>
        </div>
      </div>
    </div>
  );
}
